// eslint-disable-next-line no-restricted-imports
import {Component, PanelLifecycleContext} from 'panel';

import {Subscribable} from 'common/types/context/subscribable';
import {
  activatePersistedZendeskChat,
  activateZendeskChat,
  ChatDepartment,
  hideZendeskWidget,
} from 'common/report/external-widgets/zendesk';
import {Organization} from 'common/report/queries/app-api/user-metadata-client';
import {LocalStorageKey, Permission, ReportName} from 'common/report/constants';
import {getProjectId, getReportName, getReportUrl} from 'common/report/util';
import {getCurrentWorkspaceId, getFilteredOrgs} from 'iron/routes/global-sidenav/utils';
import {TrackingEvent} from 'iron/routes/global-sidenav/types';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';

import {ExpansionMode, ExpansionSource, PersistedState, Props, State} from './types';

const PersistedStateKey = `v1`;

/**
 * Allows external callsites to control certain functionality within the global sidenav component
 */
export class GlobalSidenavController implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];

  private declare props: Props;

  public state: State = {
    showSupportWidget: false,
    isSupportWidgetMaximized: false,
    isInviteUserModalOpen: false,
    closeInviteModalCallback: undefined,
  };

  updateProps(props: Props) {
    const prevProps: Partial<Props> = this.props ?? {};

    if (props.currentUser && props.currentUser !== prevProps.currentUser) {
      activatePersistedZendeskChat(ChatDepartment.Support, props.currentUser.name, props.currentUser.email);
    }

    this.props = props;
    this.updateComponents();
  }

  mixpanelTrack(event: TrackingEvent, properties: {[key: string]: any} = {}) {
    const commonProperties = {
      tab: getReportName(),
      'Is Collapsed': this.isCollapsed(),
      'Expansion Mode': this.getExpansionMode(),
    };

    mixpanel.track(`[Global Sidenav] ${event}`, {
      ...commonProperties,
      ...properties,
    });
  }

  updateState(stateUpdate: Partial<State>) {
    this.state = Object.assign({}, this.state, stateUpdate);
    this.updateComponents();
  }

  getUserLocalStorageHelper(): UserLocalStorageHelper<PersistedState> {
    return new UserLocalStorageHelper<PersistedState>({
      context: this.props.localStorageManager,
      namespace: LocalStorageKey.GlobalSidenav,
    });
  }

  updatePersistedState(persistedStateUpdate: Partial<PersistedState>) {
    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    if (!userId) {
      return;
    }
    const oldPersistedState = this.getPersistedState();
    this.getUserLocalStorageHelper().setItem({
      userId,
      key: PersistedStateKey,
      value: {...oldPersistedState, ...persistedStateUpdate},
    });
    this.updateComponents();
  }

  getPersistedState(): PersistedState | undefined {
    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    if (!userId) {
      return undefined;
    }
    return this.getUserLocalStorageHelper().getItem({key: PersistedStateKey, userId}) ?? undefined;
  }

  isNarrowWidthReport(): boolean {
    return [ReportName.Lexicon, ReportName.Settings, ReportName.AdminOrgSettings].includes(getReportName());
  }

  getExpansionMode(): ExpansionMode {
    return this.getPersistedState()?.expansionMode ?? ExpansionMode.Auto;
  }

  isCollapsed(): boolean {
    const expansionMode = this.getExpansionMode();
    if (expansionMode === ExpansionMode.Auto) {
      return this.isNarrowWidthReport();
    }
    return expansionMode === ExpansionMode.AlwaysCollapsed;
  }

  updateExpansionMode({
    isCollapsed,
    expansionSource,
  }: {
    isCollapsed: boolean;
    expansionSource: ExpansionSource | undefined;
  }): void {
    let newExpansionMode: ExpansionMode;
    if (isCollapsed && !this.isNarrowWidthReport()) {
      newExpansionMode = ExpansionMode.AlwaysCollapsed;
    } else if (!isCollapsed && this.isNarrowWidthReport()) {
      newExpansionMode = ExpansionMode.AlwaysExpanded;
    } else {
      newExpansionMode = ExpansionMode.Auto;
    }
    const oldExpansionMode = this.getExpansionMode();
    if (newExpansionMode !== oldExpansionMode) {
      this.updatePersistedState({expansionMode: newExpansionMode});
      this.mixpanelTrack(TrackingEvent.ExpansionToggled, {
        'Old Expansion Mode': oldExpansionMode,
        'New Expansion Mode': newExpansionMode,
        'Expansion Source': expansionSource,
      });
    }
  }

  hideCustomerSupportWidgets() {
    this.updateState({showSupportWidget: false});
    hideZendeskWidget();
  }

  openZendeskChat() {
    if (this.props.currentUser) {
      this.hideCustomerSupportWidgets();
      activateZendeskChat(ChatDepartment.Support, this.props.currentUser.name, this.props.currentUser.email);
    }
  }

  openContactSupport({isSupportWidgetMaximized}: {isSupportWidgetMaximized?: boolean} = {}) {
    this.hideCustomerSupportWidgets();
    this.updateState({
      showSupportWidget: true,
      ...(typeof isSupportWidgetMaximized === `boolean` ? {isSupportWidgetMaximized} : {}),
    });
  }

  getInviteUserOrgs(): Organization[] {
    const userOrganizations = this.props.currentUser?.organizations ?? {};
    const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(location.pathname);
    const orgId = (orgUrlMatch && parseInt(orgUrlMatch[1], 10)) || null;
    const orgs = getFilteredOrgs(userOrganizations, {permissions: [Permission.AddUser]});
    const org = orgs.find((o) => o.id === orgId);
    // if org id is specified by url and that org has `add_user` permission, show
    // invite modal for that org; otherwise show modal for all orgs with `add_user`
    // (user will choose org in modal)
    return org ? [org] : orgs;
  }

  openInviteUserModal({closeCallback}: {closeCallback?: () => void} = {}): void {
    const inviteUserOrgs = this.getInviteUserOrgs();
    if (!inviteUserOrgs.length) {
      return;
    }

    this.updateState({
      isInviteUserModalOpen: true,
      closeInviteModalCallback: closeCallback,
    });
  }

  closeInviteModal(): void {
    this.state.closeInviteModalCallback?.();
    this.updateState({
      isInviteUserModalOpen: false,
      closeInviteModalCallback: undefined,
    });
  }

  getWorkspaceBasedAppUrl(appName: string): string {
    return getReportUrl(appName, {
      projectId: this.props.currentProject?.id ?? getProjectId(),
      workspaceId: getCurrentWorkspaceId({currentProject: this.props.currentProject}),
    });
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
}
